import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'ci:inline-flex ci:items-center ci:justify-center ci:gap-2 ci:whitespace-nowrap ci:rounded-md ci:text-sm ci:font-medium ci:transition-all ci:disabled:pointer-events-none ci:disabled:opacity-50 ci:[&_svg]:pointer-events-none ci:[&_svg:not([class*=size-])]:size-4 ci:shrink-0 ci:[&_svg]:shrink-0 ci:outline-none ci:focus-visible:border-ring ci:focus-visible:ring-ring/50 ci:focus-visible:ring-[3px] ci:aria-invalid:ring-destructive/20 ci:dark:aria-invalid:ring-destructive/40 ci:aria-invalid:border-destructive',
  {
    variants: {
      variant: {
        default: 'ci:bg-primary ci:text-primary-foreground ci:hover:bg-primary/90',
        destructive:
          'ci:bg-destructive ci:text-white ci:hover:bg-destructive/90 ci:focus-visible:ring-destructive/20 ci:dark:focus-visible:ring-destructive/40 ci:dark:bg-destructive/60',
        outline:
          'ci:border ci:bg-background ci:shadow-xs ci:hover:bg-accent ci:hover:text-accent-foreground ci:dark:bg-input/30 ci:dark:border-input ci:dark:hover:bg-input/50',
        secondary: 'ci:bg-secondary ci:text-secondary-foreground ci:hover:bg-secondary/80',
        ghost: 'ci:hover:bg-accent ci:hover:text-accent-foreground ci:dark:hover:bg-accent/50',
        link: 'ci:text-primary ci:underline-offset-4 ci:hover:underline',
      },
      size: {
        default: 'ci:h-9 ci:px-4 ci:py-2 ci:has-[>svg]:px-3',
        sm: 'ci:h-8 ci:rounded-md ci:gap-1.5 ci:px-3 ci:has-[>svg]:px-2.5',
        lg: 'ci:h-10 ci:rounded-md ci:px-6 ci:has-[>svg]:px-4',
        icon: 'ci:size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot='button'
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button };
